import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateDMY } from '../utils/dateUtils';
import {
  Buildings,
  House,
  Bell,
  Users,
  SignOut,
  DeviceMobileSlash,
  List,
  X,
  Elevator,
  Shield,
  Wrench,
  Check,
  CaretDown,
  CaretRight,
  Receipt,
  Wallet,
  FileText,
  Notebook,
  Bank,
  CurrencyEur,
  ShieldCheck,
  Note,
  ArrowsClockwise,
  Calculator,
  Tag,
  UsersThree,
  CreditCard,
  Broom,
  ClipboardText,
  Certificate,
  Warning,
  ListBullets,
  GearSix,
  CalendarBlank,
  Envelope,
  FireSimple,
  HandCoins,
  ChatCircleText,
  Money,
  ListChecks,
  Lock,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from '../components/ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Building sidebar section definitions
const BUILDING_SECTIONS = [
  {
    id: 'core',
    label: null,
    items: [
      { tab: 'overview', icon: House, label: 'overview' },
    ],
  },
  {
    id: 'management',
    label: 'management',
    items: [
      { tab: 'apartments', icon: UsersThree, label: 'apartments' },
      { tab: 'invoices', icon: Receipt, label: 'invoices' },
      { tab: 'payments', icon: CreditCard, label: 'payments' },
      { tab: 'statements', icon: FileText, label: 'statements' },
      { path: '/statement-tracking', icon: Envelope, label: 'statementTracking' },
      { path: '/bank-receipts', icon: Bank, label: 'bankReceiptsPage' },
      { tab: 'reserveFundTab', icon: Shield, label: 'reserveFundTab' },
      { tab: 'repairs', icon: ArrowsClockwise, label: 'repairs' },
    ],
  },
  {
    id: 'expenses',
    label: 'expenses',
    items: [
      { tab: 'expenses', icon: Receipt, label: 'expenses' },
      { tab: 'annualExpenses', icon: Calculator, label: 'annualExpenses' },
      // Phase 348 — `categories` moved out to Settings → Expense Categories
      // (global catalogue, shared across every building).
    ],
  },
  {
    id: 'technical',
    label: 'buildingTechnical',
    items: [
      { tab: 'elevator', icon: Elevator, label: 'elevator' },
      { tab: 'elevatorCertification', icon: Certificate, label: 'elevatorCertification' },
      { tab: 'fireCertification', icon: FireSimple, label: 'fireCertification' },
      // Phase 348 — `failureReasons` moved out to Settings → Failure Reasons
      // (global list, shared across every building).
      { tab: 'insuranceSection', icon: ShieldCheck, label: 'insuranceSection' },
      // Phase 348 — `vendors` moved out to Settings → Vendors (global list).
    ],
  },
  {
    id: 'financial',
    label: 'financial',
    items: [
      { tab: 'cashReceipts', icon: CurrencyEur, label: 'cashReceipts' },
      { tab: 'bankReceipts', icon: CreditCard, label: 'bankReceipts' },
      { tab: 'bankDetails', icon: CreditCard, label: 'bankDetails', nested: true },
      // Phase 920.3 — Owner Advances: money the building owes to owners
      // who paid common expenses on behalf of the building.
      { tab: 'ownerAdvances', icon: HandCoins, label: 'ownerAdvances' },
    ],
  },
  {
    id: 'documents',
    label: 'documents',
    items: [
      { tab: 'meetings', icon: Notebook, label: 'meetings' },
      // Phase 352 — `agendaTemplates` moved to global Settings → General Meetings.
      { tab: 'ownerHistory', icon: Users, label: 'ownerHistory' },
      // Phase 349 — `emailTemplates` per-building entry moved to global
      // Settings → Email Templates. Phase 711 — per-building tab renamed
      // from "Emails" to "Email Center" so it aligns with the new global
      // mailbox module. The tab still hosts the existing send-flows
      // (Statements / Approvals / Reminders / Bulk Emailing); the new
      // mailbox UI lives under the top-nav /email-center route.
      { tab: 'emails', icon: Envelope, label: 'emailCenter' },
      { tab: 'communications', icon: ChatCircleText, label: 'communications' },
      { tab: 'collections', icon: Warning, label: 'collections' },
      { tab: 'ownerFeedback', icon: ChatCircleText, label: 'ownerFeedback', perm: 'building_management.owner_feedback.view' },
    ],
  },
  {
    id: 'settings',
    label: null,
    items: [],
  },
];

// ---------------------------------------------------------------------------
// Phase 708d — Notification categorisation (#5).
// Buckets every notification.type onto one of 7 named categories the admin
// requested. The bell dropdown renders a filter row across the top and the
// list below it is filtered by the active category.
// ---------------------------------------------------------------------------
const NOTIF_CATEGORIES = [
  { id: 'all', label: 'All', emoji: '🔔' },
  { id: 'approvals', label: 'Approvals', emoji: '📝' },
  { id: 'payments', label: 'Payments', emoji: '💳' },
  { id: 'bills', label: 'Bills', emoji: '🧾' },
  { id: 'repairs', label: 'Repairs', emoji: '🔧' },
  { id: 'reserve_fund', label: 'Reserve Fund', emoji: '🏦' },
  { id: 'emails', label: 'Emails', emoji: '✉️' },
  { id: 'system', label: 'System', emoji: '⚙️' },
];

function categoryOf(notif) {
  const t = String(notif.type || '').toLowerCase();
  const action = String(notif.action || '').toLowerCase();
  if (t === 'approval_action' || t === 'approval') return 'approvals';
  if (t.includes('payment') || action.includes('payment')) return 'payments';
  if (t.includes('bill') || action.includes('bill')) return 'bills';
  if (t.includes('repair') || action.includes('repair')) return 'repairs';
  if (t.includes('reserve') || action.includes('reserve')) return 'reserve_fund';
  if (t.includes('email') || t.includes('mail') || action.includes('mail')) return 'emails';
  return 'system';
}

function filterNotificationsByCategory(list, category) {
  if (category === 'all') return list;
  return list.filter(n => categoryOf(n) === category);
}

function NotificationCategoryFilter({ notifications, active, onChange }) {
  const counts = {};
  for (const n of notifications) {
    const c = categoryOf(n);
    counts[c] = (counts[c] || 0) + (n.is_read ? 0 : 1);
  }
  counts.all = notifications.filter(n => !n.is_read).length;
  return (
    <div className="flex items-center gap-1 overflow-x-auto px-2 py-2 border-b border-zinc-200 bg-white text-xs" data-testid="notif-category-filter">
      {NOTIF_CATEGORIES.map(cat => {
        const c = counts[cat.id] || 0;
        const isActive = active === cat.id;
        return (
          <button
            key={cat.id}
            onClick={() => onChange(cat.id)}
            className={`flex-shrink-0 px-2 py-1 rounded-none border ${isActive ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400'}`}
            data-testid={`notif-cat-${cat.id}`}
          >
            <span className="mr-1">{cat.emoji}</span>
            <span>{cat.label}</span>
            {c > 0 && (
              <span className={`ml-1 inline-block px-1.5 text-[10px] font-mono ${isActive ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-700'}`}>{c}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}



export default function Layout({ children, hideLayout = false }) {
  const { user, logout, logoutAll, isAdmin, hasModule, hasPerm } = useAuth();
  const visibleSections = BUILDING_SECTIONS.map((s) => ({ ...s, items: s.items.filter((i) => !i.perm || hasPerm(i.perm)) }));
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  // Phase 708d — Notification category filter (#5). Maps the bell entries
  // onto 7 buckets so the admin can drill into one stream at a time.
  const [notifCategory, setNotifCategory] = useState('all');
  const [buildings, setBuildings] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({});
  const [buildingApartments, setBuildingApartments] = useState([]);
  const [apartmentsExpanded, setApartmentsExpanded] = useState(false);
  const [companyName, setCompanyName] = useState('');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleLogoutAll = async () => {
    if (!window.confirm('Αποσύνδεση από όλες τις συσκευές; Θα χρειαστεί νέα σύνδεση παντού.')) return;
    try {
      await logoutAll();
      toast.success('Αποσυνδεθήκατε από όλες τις συσκευές');
      navigate('/login');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Αποτυχία αποσύνδεσης');
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications`, { withCredentials: true });
      setNotifications(res.data);
    } catch {}
  }, []);

  const fetchBuildings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/buildings`, { withCredentials: true });
      setBuildings(res.data || []);
      if (res.data?.length && !selectedBuildingId) {
        setSelectedBuildingId(res.data[0].building_id);
      }
    } catch {}
  }, [selectedBuildingId]);

  useEffect(() => {
    fetchNotifications();
    fetchBuildings();
    axios.get(`${API}/company-profile`, { withCredentials: true }).then(r => { if (r.data?.company_name) setCompanyName(r.data.company_name); }).catch(() => {});
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchBuildings]);

  // Detect building from URL
  const buildingMatch = location.pathname.match(/\/buildings\/([^/]+)/);
  const isStatementView = location.pathname.endsWith('/statement');
  const isApartmentView = location.pathname.startsWith('/apartments/');
  const isTasksView = location.pathname === '/tasks';
  const isFailureReasonsView = location.pathname === '/failure-reasons';
  const isVendorsView = location.pathname.startsWith('/vendors');
  const isAgendaTemplatesView = location.pathname === '/agenda-templates';
  const isBuildingView = (!!buildingMatch && !isStatementView) || isApartmentView || isTasksView || isFailureReasonsView || isVendorsView || isAgendaTemplatesView;
  const currentBuildingId = buildingMatch ? buildingMatch[1] : null;
  const [apartmentBuildingId, setApartmentBuildingId] = useState(null);

  // For apartment pages, fetch the apartment to get building_id
  useEffect(() => {
    if (isApartmentView) {
      const aptId = location.pathname.split('/apartments/')[1]?.split('?')[0];
      if (aptId) {
        axios.get(`${API}/apartments/${aptId}`, { withCredentials: true })
          .then(res => {
            if (res.data?.building_id) {
              setApartmentBuildingId(res.data.building_id);
              if (res.data.building_id !== selectedBuildingId) {
                setSelectedBuildingId(res.data.building_id);
              }
            }
          })
          .catch(() => {});
      }
    }
  }, [location.pathname, isApartmentView]);

  const effectiveBuildingId = currentBuildingId || apartmentBuildingId || selectedBuildingId;

  // Fetch apartments for the current building
  useEffect(() => {
    if (effectiveBuildingId) {
      axios.get(`${API}/buildings/${effectiveBuildingId}/apartments`, { withCredentials: true })
        .then(res => setBuildingApartments(res.data || []))
        .catch(() => setBuildingApartments([]));
    } else {
      setBuildingApartments([]);
    }
  }, [effectiveBuildingId]);

  // Auto-expand apartments list when on apartment page
  useEffect(() => {
    if (isApartmentView) setApartmentsExpanded(true);
  }, [isApartmentView]);

  useEffect(() => {
    if (currentBuildingId && currentBuildingId !== selectedBuildingId) {
      setSelectedBuildingId(currentBuildingId);
    }
  }, [currentBuildingId, selectedBuildingId]);

  // Get active tab from URL
  const activeTab = new URLSearchParams(location.search).get('tab') || (isApartmentView ? '' : 'overview');
  const currentApartmentId = isApartmentView ? location.pathname.split('/apartments/')[1]?.split('?')[0] : null;

  // Auto-expand section containing active tab
  useEffect(() => {
    if (isBuildingView) {
      const sectionWithTab = BUILDING_SECTIONS.find(s => s.items.some(i => i.tab === activeTab));
      if (sectionWithTab && sectionWithTab.id && collapsedSections[sectionWithTab.id]) {
        setCollapsedSections(prev => ({ ...prev, [sectionWithTab.id]: false }));
      }
    }
  }, [activeTab, isBuildingView]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const markAsRead = async (notifId) => {
    try {
      await axios.post(`${API}/notifications/${notifId}/read`, {}, { withCredentials: true });
      fetchNotifications();
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-all-read`, {}, { withCredentials: true });
      fetchNotifications();
    } catch {}
  };

  const goToTab = (tab) => {
    const bid = effectiveBuildingId;
    if (bid) {
      navigate(`/buildings/${bid}?tab=${tab}`);
    }
  };

  const toggleSection = (sectionId) => {
    setCollapsedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  };

  const selectedBuilding = buildings.find(b => b.building_id === effectiveBuildingId);

  const notifDropdown = (
    <div className="relative">
      <button
        onClick={() => setNotifOpen(!notifOpen)}
        className="relative p-2 hover:bg-zinc-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={22} weight={unreadCount > 0 ? 'fill' : 'regular'} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold flex items-center justify-center rounded-full" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      {notifOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
          <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-zinc-200 shadow-xl z-50 max-h-[520px] flex flex-col" data-testid="notification-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50">
              <span className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('notifications')}</span>
              {unreadCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="mark-all-read-btn">
                  {t('markAllRead')}
                </button>
              )}
            </div>
            {/* Phase 708d — Category filter tabs. Maps notification.type
                onto the 7 user-requested buckets. Counts shown per tab. */}
            <NotificationCategoryFilter
              notifications={notifications}
              active={notifCategory}
              onChange={setNotifCategory}
            />
            <div className="overflow-y-auto flex-1">
              {(() => {
                const filtered = filterNotificationsByCategory(notifications, notifCategory);
                if (filtered.length === 0) {
                  return <div className="py-10 text-center text-zinc-400 text-sm">{t('noNotifications')}</div>;
                }
                return filtered.slice(0, 30).map((notif) => (
                  <div
                    key={notif.notification_id}
                    className={`px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer transition-colors ${!notif.is_read ? 'bg-blue-50/50' : ''}`}
                    onClick={() => {
                      if (!notif.is_read) markAsRead(notif.notification_id);
                      if (notif.link_path) {
                        setNotifOpen(false);
                        navigate(notif.link_path);
                      }
                    }}
                    data-testid={`notif-item-${notif.notification_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${!notif.is_read ? 'bg-blue-500' : 'bg-transparent'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm leading-tight ${!notif.is_read ? 'font-semibold text-zinc-900' : 'text-zinc-600'}`}>
                          {notif.title || notif.message}
                        </p>
                        {notif.title && notif.message && (
                          <p className="text-xs text-zinc-500 mt-0.5 truncate">{notif.message}</p>
                        )}
                        <p className="text-[10px] text-zinc-400 mt-1">
                          {notif.created_at ? formatDateDMY(notif.created_at) : ''}
                        </p>
                      </div>
                      {!notif.is_read && (
                        <button onClick={(e) => { e.stopPropagation(); markAsRead(notif.notification_id); }} className="p-1 hover:bg-blue-100 text-blue-500 transition-colors flex-shrink-0" title="Mark as read">
                          <Check size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ));
              })()}
            </div>
            {notifications.length > 0 && (
              <div className="border-t border-zinc-200 px-4 py-2 bg-zinc-50">
                <Link to="/notifications" onClick={() => setNotifOpen(false)} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="view-all-notifications">
                  {t('viewAll')}
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  // Building sidebar content
  const buildingSidebar = isBuildingView ? (
    <aside className="hidden md:flex flex-col fixed left-0 top-14 bottom-0 w-56 bg-white border-r border-zinc-200 z-20 overflow-y-auto" data-testid="building-sidebar">
      {/* Building name header */}
      <div className="px-4 py-4 border-b border-zinc-200 bg-zinc-50">
        <p className="text-xs font-medium text-zinc-400 uppercase tracking-wider">{t('building')}</p>
        <p className="text-sm font-bold text-zinc-900 mt-1 truncate" style={{ fontFamily: 'var(--font-heading)' }}>
          {selectedBuilding?.name || '...'}
        </p>
      </div>

      {/* Collapsible sections */}
      <nav className="flex-1 py-2">
        {visibleSections.map((section) => {
          const isCollapsed = !!collapsedSections[section.id];
          const hasActiveItem = section.items.some(i => {
            if (i.tab) return activeTab === i.tab || (isApartmentView && i.tab === 'apartments');
            if (i.path) return location.pathname === i.path;
            return false;
          });

          return (
            <div key={section.id} className="mb-0.5">
              {section.label ? (
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`w-full flex items-center justify-between px-4 py-2 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                    hasActiveItem ? 'text-zinc-900' : 'text-zinc-400 hover:text-zinc-600'
                  }`}
                  data-testid={`section-toggle-${section.id}`}
                >
                  <span>{t(section.label)}</span>
                  {isCollapsed ? <CaretRight size={12} /> : <CaretDown size={12} />}
                </button>
              ) : (
                <div className="h-1" />
              )}

              {!isCollapsed && (
                <div className="space-y-0">
                  {section.items.map((item) => {
                    // Special handling for Apartments — collapsible sub-list
                    if (item.tab === 'apartments') {
                      const isAptActive = activeTab === 'apartments' || isApartmentView;
                      return (
                        <div key="apartments-section">
                          <div className="flex items-center">
                            <button
                              onClick={() => goToTab('apartments')}
                              className={`flex-1 flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left border-l-[3px] ${
                                isAptActive && !currentApartmentId
                                  ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                                  : isAptActive
                                  ? 'border-l-zinc-900 text-zinc-700 font-medium'
                                  : 'border-l-transparent text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700'
                              }`}
                              data-testid="tab-apartments"
                            >
                              <UsersThree size={18} weight={isAptActive ? 'fill' : 'regular'} />
                              <span>{t('apartments')}</span>
                            </button>
                            {buildingApartments.length > 0 && (
                              <button
                                onClick={() => setApartmentsExpanded(!apartmentsExpanded)}
                                className="px-2 py-2 text-zinc-400 hover:text-zinc-600 transition-colors"
                                data-testid="apartments-expand-toggle"
                              >
                                {apartmentsExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
                              </button>
                            )}
                          </div>
                          {apartmentsExpanded && buildingApartments.length > 0 && (
                            <div className="ml-4 border-l border-zinc-200">
                              {buildingApartments
                                .sort((a, b) => (a.apartment_number || '').localeCompare(b.apartment_number || '', undefined, { numeric: true }))
                                .map(apt => {
                                  // Phase 303 — SINGLE SOURCE OF TRUTH.
                                  // Use the canonical balance computed server-side
                                  // (apt.balance is set by compute_apartment_balance:
                                  // real_payments + credit_notes − invoices − OB).
                                  // Convention: NEGATIVE = debt, POSITIVE = credit.
                                  // Display matches the Statement + Invoices tab + PDF.
                                  const balance = Math.round((apt.balance || 0) * 100) / 100;
                                  const isCurrent = currentApartmentId === apt.apartment_id;
                                  return (
                                    <button
                                      key={apt.apartment_id}
                                      onClick={() => navigate(`/apartments/${apt.apartment_id}`)}
                                      className={`w-full flex items-center justify-between px-3 py-1.5 text-left transition-colors ${
                                        isCurrent
                                          ? 'bg-zinc-100 text-zinc-900 font-semibold'
                                          : 'text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700'
                                      }`}
                                      data-testid={`sidebar-apt-${apt.apartment_id}`}
                                    >
                                      <span className="text-xs truncate">
                                        <span className="font-mono font-semibold">#{apt.apartment_number}</span>
                                        {' '}<span className="text-zinc-400">{apt.owner_name || ''}</span>
                                      </span>
                                      <span className={`text-[10px] font-mono flex-shrink-0 ml-1 ${
                                        balance < -0.01 ? 'text-red-500' : balance > 0.01 ? 'text-emerald-600' : 'text-zinc-300'
                                      }`} data-testid={`sidebar-apt-balance-${apt.apartment_id}`}>
                                        {Math.abs(balance) < 0.005 ? '—' : `${balance < 0 ? '-' : ''}€${Math.abs(balance).toFixed(2)}`}
                                      </span>
                                    </button>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      );
                    }

                    const isItemActive = item.tab
                      ? (activeTab === item.tab)
                      : location.pathname === item.path;
                    const indent = item.nested ? 'pl-10' : 'px-4';
                    return (
                      <button
                        key={item.tab || item.path}
                        onClick={() => item.path ? navigate(item.path) : goToTab(item.tab)}
                        className={`w-full flex items-center gap-3 ${indent} py-2 text-sm transition-colors text-left border-l-[3px] ${
                          isItemActive
                            ? 'border-l-zinc-900 bg-zinc-100 text-zinc-900 font-semibold'
                            : 'border-l-transparent text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700'
                        }`}
                        data-testid={`tab-${item.tab || item.label}`}
                      >
                        <item.icon size={item.nested ? 15 : 18} weight={isItemActive ? 'fill' : 'regular'} />
                        <span className={item.nested ? 'text-xs' : ''}>{t(item.label)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  ) : null;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Top Bar — Fixed */}
      <header className="hidden md:flex fixed top-0 left-0 right-0 bg-white border-b border-zinc-200 z-30 h-14 items-center justify-between px-6">
        {/* Left: Logo + Dashboard + Building Selector */}
        <div className="flex items-center gap-1">
          <Link to="/dashboard" className="flex items-center gap-2.5 mr-4 hover:opacity-80 transition-opacity" data-testid="nav-logo">
            <Buildings size={26} weight="fill" className="text-zinc-900" />
            <span className="text-base font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              CleanPro
            </span>
          </Link>

          <div className="w-px h-6 bg-zinc-200 mx-1" />

          {/* Building Management Group */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-zinc-50 ${
                location.pathname === '/dashboard' || location.pathname.startsWith('/buildings') ? 'text-zinc-900 font-semibold' : 'text-zinc-600'
              }`} data-testid="topbar-building-management">
                <Buildings size={18} weight={(location.pathname === '/dashboard' || location.pathname.startsWith('/buildings')) ? 'fill' : 'regular'} />
                <span>{t('buildingManagement')}</span>
                <CaretDown size={14} className="text-zinc-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 max-h-[70vh] overflow-y-auto">
              <DropdownMenuItem onClick={() => navigate('/dashboard')} className={`font-semibold ${location.pathname === '/dashboard' ? 'bg-zinc-100 text-zinc-900' : ''}`} data-testid="topbar-dashboard">
                <House size={16} className="mr-2 text-zinc-700" />
                {t('dashboard')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/buildings')} className={`font-semibold ${location.pathname === '/buildings' && !location.pathname.match(/\/buildings\/\w/) ? 'bg-zinc-100 text-zinc-900' : ''}`} data-testid="manage-buildings-link">
                <Buildings size={16} className="mr-2 text-zinc-700" />
                {t('manageBuildings')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/contacts')} className={`font-semibold ${location.pathname === '/contacts' ? 'bg-zinc-100 text-zinc-900' : ''}`} data-testid="contacts-directory-link">
                <Buildings size={16} className="mr-2 text-zinc-700" />
                {t('contactDirectory') || 'Contact Directory'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/statement-tracking')} className={`font-semibold ${location.pathname === '/statement-tracking' ? 'bg-zinc-100 text-zinc-900' : ''}`} data-testid="topbar-statement-tracking">
                <Envelope size={16} className="mr-2 text-zinc-700" />
                {t('statementTracking')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/bank-receipts')} className={`font-semibold ${location.pathname === '/bank-receipts' ? 'bg-zinc-100 text-zinc-900' : ''}`} data-testid="topbar-bank-receipts">
                <Bank size={16} className="mr-2 text-zinc-700" />
                {t('bankReceiptsPage')}
              </DropdownMenuItem>
              {hasPerm('building_management.owner_feedback.view') && (
                <DropdownMenuItem onClick={() => navigate('/owner-feedback')} className={`font-semibold ${location.pathname === '/owner-feedback' ? 'bg-zinc-100 text-zinc-900' : ''}`} data-testid="owner-feedback-overview-link">
                  <ChatCircleText size={16} className="mr-2 text-zinc-700" />
                  {t('ownerFeedback')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {buildings.filter(b => b.building_type !== 'cleaning_only').length > 0 && (
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold flex items-center gap-1.5">
                    <Buildings size={12} /> {t('managedBuildings')}
                  </DropdownMenuLabel>
                  {buildings.filter(b => b.building_type !== 'cleaning_only').map(b => (
                    <DropdownMenuItem
                      key={b.building_id}
                      onClick={() => { setSelectedBuildingId(b.building_id); navigate(`/buildings/${b.building_id}`); }}
                      className={selectedBuildingId === b.building_id ? 'bg-zinc-100 font-semibold' : ''}
                      data-testid={`building-option-${b.building_id}`}
                    >
                      <Buildings size={14} className="mr-2 text-blue-500" />
                      <span className="truncate">{b.name}</span>
                      {selectedBuildingId === b.building_id && <Check size={14} className="ml-auto text-emerald-600 flex-shrink-0" />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              )}
              {buildings.filter(b => b.building_type === 'cleaning_only').length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold flex items-center gap-1.5">
                      <Broom size={12} /> {t('cleaningClients')}
                    </DropdownMenuLabel>
                    {buildings.filter(b => b.building_type === 'cleaning_only').map(b => (
                      <DropdownMenuItem
                        key={b.building_id}
                        onClick={() => { setSelectedBuildingId(b.building_id); navigate(`/buildings/${b.building_id}/statement`); }}
                        className={selectedBuildingId === b.building_id ? 'bg-zinc-100 font-semibold' : ''}
                        data-testid={`building-option-${b.building_id}`}
                      >
                        <Broom size={14} className="mr-2 text-amber-500" />
                        <span className="truncate">{b.name}</span>
                        {selectedBuildingId === b.building_id && <Check size={14} className="ml-auto text-emerald-600 flex-shrink-0" />}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuGroup>
                </>
              )}
              {buildings.length === 0 && (
                <DropdownMenuItem disabled>{t('noBuildings')}</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="w-px h-6 bg-zinc-200 mx-1" />

          <Link
            to="/cleaning-management"
            className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-zinc-50 ${
              location.pathname.startsWith('/cleaning-management') || location.pathname === '/cleaning-history' ? 'text-zinc-900 font-semibold' : 'text-zinc-600'
            } ${isAdmin || hasModule('cleaning') ? '' : 'hidden'}`}
            data-testid="topbar-cleaning-management"
          >
            <Users size={18} weight={location.pathname.startsWith('/cleaning-management') || location.pathname === '/cleaning-history' ? 'fill' : 'regular'} />
            <span>{t('cleaningManagement')}</span>
          </Link>

          <Link
            to="/finance"
            className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-zinc-50 ${
              location.pathname === '/finance' || location.pathname === '/sales' ? 'text-zinc-900 font-semibold' : 'text-zinc-600'
            } ${isAdmin || hasModule('finance') ? '' : 'hidden'}`}
            data-testid="topbar-finance"
          >
            <Wallet size={18} weight={(location.pathname === '/finance' || location.pathname === '/sales') ? 'fill' : 'regular'} />
            <span>{t('finance')}</span>
          </Link>

          {/* Phase 413 — Sales now lives inside Finance → Sales (top-nav entry removed) */}

          {/* Phase 1016 — Bills top-nav entry removed. The Bills module is
              being rebuilt from scratch per user request; the route is
              temporarily gated to a placeholder (see App.js). Backend
              endpoints and DB data are preserved. */}

          {/* Phase 1018 — Task Management promoted to a top-level module,
              cleanly separated from Calendar. Points at the standalone
              tasks-list page (was reachable only via a button inside
              Calendar). */}
          <Link
            to="/calendar/tasks-list"
            className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-zinc-50 ${
              location.pathname === '/calendar/tasks-list' || location.pathname === '/calendar/tasks' ? 'text-zinc-900 font-semibold' : 'text-zinc-600'
            } ${isAdmin || hasModule('tasks') ? '' : 'hidden'}`}
            data-testid="topbar-task-management"
          >
            <ListChecks size={18} weight={(location.pathname === '/calendar/tasks-list' || location.pathname === '/calendar/tasks') ? 'fill' : 'regular'} />
            <span>{language === 'el' ? 'Διαχείριση Εργασιών' : 'Task Management'}</span>
          </Link>

          <Link
            to="/calendar"
            className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-zinc-50 ${
              location.pathname === '/calendar' ? 'text-zinc-900 font-semibold' : 'text-zinc-600'
            } ${isAdmin || hasModule('calendar') ? '' : 'hidden'}`}
            data-testid="topbar-calendar"
          >
            <CalendarBlank size={18} weight={location.pathname === '/calendar' ? 'fill' : 'regular'} />
            <span>{language === 'el' ? 'Ημερολόγιο' : 'Calendar'}</span>
          </Link>

          {/* Phase 711 — Email Center: global mailbox (Inbox/Sent/Drafts/Archive/Trash) */}
          <Link
            to="/email-center"
            className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-zinc-50 ${
              location.pathname === '/email-center' ? 'text-zinc-900 font-semibold' : 'text-zinc-600'
            } ${isAdmin || hasModule('email_center') ? '' : 'hidden'}`}
            data-testid="topbar-email-center"
          >
            <Envelope size={18} weight={location.pathname === '/email-center' ? 'fill' : 'regular'} />
            <span>{language === 'el' ? 'Κέντρο Email' : 'Email Center'}</span>
          </Link>

          {/* Phase 830 — Payroll module (fully independent HR/Payroll app) */}
          <Link
            to="/payroll"
            className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-zinc-50 ${
              location.pathname.startsWith('/payroll') ? 'text-zinc-900 font-semibold' : 'text-zinc-600'
            } ${isAdmin || hasModule('payroll') ? '' : 'hidden'}`}
            data-testid="topbar-payroll"
          >
            <Money size={18} weight={location.pathname.startsWith('/payroll') ? 'fill' : 'regular'} />
            <span>{language === 'el' ? 'Μισθοδοσία' : 'Payroll'}</span>
          </Link>

        </div>

        {/* Right: Settings, Notifications, Language, User */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/settings')}
            className={`p-2 hover:bg-zinc-100 transition-colors ${location.pathname === '/settings' ? 'bg-zinc-100 text-zinc-900' : 'text-zinc-500'} ${isAdmin || hasModule('settings') ? '' : 'hidden'}`}
            title={t('settings')}
            data-testid="topbar-settings-btn"
          >
            <GearSix size={20} />
          </button>
          {notifDropdown}
          <button
            onClick={toggleLanguage}
            className="px-3 py-1.5 border border-zinc-200 text-xs font-medium hover:bg-zinc-50 transition-colors"
            data-testid="desktop-language-toggle"
          >
            {language === 'en' ? 'Ελληνικά' : 'English'}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-3 py-2 hover:bg-zinc-50 transition-colors" data-testid="user-menu">
                <div className="w-8 h-8 bg-zinc-200 flex items-center justify-center text-zinc-700 font-semibold text-sm">
                  {(companyName || user?.name || 'U').charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-medium">{companyName || user?.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {/* Phase 1020 — Account & admin quick actions */}
              <DropdownMenuItem onClick={() => navigate('/account/change-password')} data-testid="dropdown-change-password">
                <Lock size={16} className="mr-2" /> Change Password
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate('/admin/users')} data-testid="dropdown-manage-users">
                  <Users size={16} className="mr-2" /> Manage Users
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate('/admin/data-sync')} data-testid="dropdown-data-sync">
                  <Lock size={16} className="mr-2" /> Data Health &amp; Sync
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleLogoutAll} className="text-red-600" data-testid="dropdown-logout-all">
                <DeviceMobileSlash size={18} className="mr-2" />
                {language === 'el' ? 'Αποσύνδεση από όλες τις συσκευές' : 'Log out of all devices'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="dropdown-logout">
                <SignOut size={18} className="mr-2" />
                {t('logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white border-b border-zinc-200 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="nav-logo-mobile">
            <Buildings size={24} weight="fill" className="text-zinc-900" />
            <span className="font-bold" style={{ fontFamily: 'var(--font-heading)' }}>CleanPro</span>
          </Link>
          <div className="flex items-center gap-2">
            {notifDropdown}
            <button onClick={toggleLanguage} className="px-2 py-1 border border-zinc-200 text-xs font-medium" data-testid="mobile-language-toggle">
              {language === 'en' ? 'EL' : 'EN'}
            </button>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2" data-testid="mobile-menu-btn">
              {sidebarOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white overflow-y-auto">
            <div className="p-4 pt-16">
              <Link to="/dashboard" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium hover:bg-zinc-50 transition-colors">
                <House size={20} />
                <span>{t('dashboard')}</span>
              </Link>
              <Link to="/buildings" onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium hover:bg-zinc-50 transition-colors">
                <Buildings size={20} />
                <span>{t('buildings')}</span>
              </Link>

              {/* Building sections for mobile */}
              {isBuildingView && (
                <>
                  <div className="border-t border-zinc-200 my-3" />
                  <p className="px-3 text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">{selectedBuilding?.name}</p>
                  {visibleSections.map((section) => (
                    <div key={section.id}>
                      {section.label && (
                        <p className="px-3 pt-3 pb-1 text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{t(section.label)}</p>
                      )}
                      {section.items.map((item) => (
                        <button
                          key={item.tab}
                          onClick={() => { goToTab(item.tab); setSidebarOpen(false); }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm transition-colors text-left ${
                            activeTab === item.tab ? 'bg-zinc-100 text-zinc-900 font-semibold' : 'text-zinc-600 hover:bg-zinc-50'
                          }`}
                        >
                          <item.icon size={18} />
                          <span>{t(item.label)}</span>
                        </button>
                      ))}
                    </div>
                  ))}
                </>
              )}

              {/* System links mobile */}
              <div className="border-t border-zinc-200 my-3" />
              <p className="px-3 text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">{t('system')}</p>
              {[
                { path: '/cleaning-management', icon: Broom, label: 'cleaningManagement' },
                { path: '/finance', icon: Wallet, label: 'finance' },
                /* Phase 1016 — Bills removed from mobile menu (module being rebuilt) */
                /* Phase 1018 — Task Management is now a top-level module. */
                { path: '/calendar/tasks-list', icon: ListChecks, label: language === 'el' ? 'Διαχείριση Εργασιών' : 'Task Management', literal: true },
                { path: '/calendar', icon: CalendarBlank, label: language === 'el' ? 'Ημερολόγιο' : 'Calendar', literal: true },
                ...(isAdmin ? [{ path: '/users', icon: Users, label: 'users' }] : []),
              ].map(item => (
                <Link key={item.path} to={item.path} onClick={() => setSidebarOpen(false)} className="flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-600 hover:bg-zinc-50 transition-colors">
                  <item.icon size={18} />
                  <span>{item.literal ? item.label : t(item.label)}</span>
                </Link>
              ))}
            </div>
            <div className="p-4 border-t border-zinc-200">
              <button onClick={handleLogoutAll} className="flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full transition-colors" data-testid="mobile-logout-all">
                <DeviceMobileSlash size={20} />
                <span>{language === 'el' ? 'Αποσύνδεση από όλες τις συσκευές' : 'Log out of all devices'}</span>
              </button>
              <button onClick={handleLogout} className="flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full transition-colors">
                <SignOut size={20} />
                <span>{t('logout')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Building Sidebar — Desktop Only */}
      {buildingSidebar}

      {/* Main Content */}
      <main className={`min-h-screen pt-14 md:pt-14 pb-20 md:pb-0 ${isBuildingView ? 'md:ml-56' : ''}`}>
        <div className="p-4 md:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden bottom-nav" data-testid="mobile-bottom-nav">
        <Link to="/dashboard" className={`bottom-nav-item ${location.pathname === '/dashboard' ? 'active' : ''}`}>
          <House size={24} weight={location.pathname === '/dashboard' ? 'fill' : 'regular'} />
          <span>{t('dashboard')}</span>
        </Link>
        <Link to="/buildings" className={`bottom-nav-item ${location.pathname === '/buildings' ? 'active' : ''}`}>
          <Buildings size={24} weight={location.pathname === '/buildings' ? 'fill' : 'regular'} />
          <span>{t('buildings')}</span>
        </Link>
        {isBuildingView && (
          <button onClick={() => setSidebarOpen(true)} className="bottom-nav-item">
            <List size={24} />
            <span>{t('menu')}</span>
          </button>
        )}
      </nav>
    </div>
  );
}
